import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageBack } from "@/components/PageBack";
import { deleteMyAccount, getAccountDeletionStatus } from "@/lib/account-deletion.functions";
import { AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings/account")({
  component: AccountSettings,
});

function AccountSettings() {
  const navigate = useNavigate();
  const status = useServerFn(getAccountDeletionStatus);
  const remove = useServerFn(deleteMyAccount);
  const [blockers, setBlockers] = useState<{ organization: string; members: number }[] | null>(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await status({} as never);
        if (!cancelled) setBlockers(res.blockers);
      } catch {
        if (!cancelled) setBlockers([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const blocked = !!blockers?.length;

  const onDelete = async () => {
    if (confirm !== "DELETE") return;
    setBusy(true);
    try {
      await remove({ data: { confirm: "DELETE" } });
      await supabase.auth.signOut();
      toast.success("Your account has been deleted.");
      navigate({ to: "/", replace: true });
    } catch (e) {
      const msg = e instanceof Response ? await e.text() : (e as Error)?.message;
      toast.error(msg || "We couldn't delete your account. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <PageBack fallbackTo="/settings" />
      <h1 className="font-display text-2xl font-bold md:text-3xl">Account</h1>
      <p className="text-sm text-muted-foreground">Manage or permanently delete your MissionReady account.</p>

      <div className="mt-6 max-w-2xl rounded-xl border border-destructive/40 bg-card p-5">
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <h2 className="font-display text-lg font-semibold">Delete my account</h2>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          This permanently removes your sign-in, your profile, and your access to every workspace you belong to.
          It cannot be undone.
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Deleted right away: your login, name, photo, contact details, and workspace access.</li>
          <li>Kept by your employer: shift, patrol, checkpoint, incident, and report records you created, because they are the company's operational and legal records.</li>
          <li>Billing records are kept for up to 7 years where the law requires it.</li>
        </ul>

        {blocked && (
          <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3 text-sm">
            You still own {blockers!.map((b) => `${b.organization} (${b.members} other member${b.members === 1 ? "" : "s"})`).join(", ")}.
            Hand ownership to someone else on the Team page first, then come back here.
          </div>
        )}

        <div className="mt-5 space-y-2">
          <Label htmlFor="confirm-delete">Type DELETE to confirm</Label>
          <Input
            id="confirm-delete"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
            disabled={blocked || busy}
          />
        </div>

        <Button
          variant="destructive"
          className="mt-4"
          disabled={blocked || busy || confirm !== "DELETE"}
          onClick={onDelete}
        >
          {busy ? "Deleting…" : "Delete my account permanently"}
        </Button>
      </div>
    </div>
  );
}
